const KATEX_SELECTOR = '[data-katex], .ql-formula'

const resolveLatex = (element: Element): string => {
  const text =
    (element.getAttribute('data-katex') ||
      element.getAttribute('data-value') ||
      element.getAttribute('data-formula') ||
      element.getAttribute('data-latex') ||
      element.textContent ||
      '')?.trim() || ''

  if (text && !element.getAttribute('data-katex')) {
    element.setAttribute('data-katex', text)
  }

  return text
}

const renderKatexElements = (root: ParentNode | Document = document) => {
  if (typeof window === 'undefined') {
    return
  }

  const katex: typeof import('katex') | undefined = (window as any).katex
  if (!katex || typeof katex.render !== 'function') {
    return
  }

  const nodes = root.querySelectorAll<HTMLElement>(KATEX_SELECTOR)
  nodes.forEach((node) => {
    if (node.getAttribute('data-katex-rendered') === '1') {
      return
    }

    const tex = resolveLatex(node)
    if (!tex) {
      return
    }

    const displayMode =
      node.getAttribute('data-katex-display') === '1' || node.tagName === 'DIV'

    try {
      katex.render(tex, node, {throwOnError: false, displayMode})
      node.setAttribute('data-katex-rendered', '1')
    } catch (error) {
      if (!node.innerHTML) {
        node.textContent = tex
      }
    }
  })
}

export const initializeKatexAutoRenderer = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const win = window as any
  if (win.__nosiKatexAutoRendererInitialized) {
    return
  }
  win.__nosiKatexAutoRendererInitialized = true

  let scheduled = false
  const scheduleRender = () => {
    if (scheduled) {
      return
    }
    scheduled = true
    const runner =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame
        : (cb: FrameRequestCallback) => window.setTimeout(cb, 16)

    runner(() => {
      scheduled = false
      renderKatexElements(document)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRender, {once: true})
  } else {
    scheduleRender()
  }

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((records) => {
      if (!records || records.length === 0) {
        return
      }

      const shouldRender = records.some((record) => {
        if (record.type === 'childList') {
          return Array.from(record.addedNodes).some(
            (node) =>
              node instanceof Element &&
              (node.matches(KATEX_SELECTOR) || node.querySelector(KATEX_SELECTOR))
          )
        }

        if (record.type === 'attributes' && record.target instanceof Element) {
          return (record.target as Element).matches(KATEX_SELECTOR)
        }

        return false
      })

      if (shouldRender) {
        scheduleRender()
      }
    })

    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-katex', 'data-value', 'data-formula', 'data-latex'],
    })
  }
}
